import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  ArrowRight, Download, Building2, Layers, Package, Calculator, 
  FileText, CheckCircle, AlertTriangle, Lightbulb, ChevronDown, ChevronUp, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + "/api/buildings";

// بيانات المواد حسب الكود السعودي للبناء SBC
const MATERIALS_DATA = {
  foundation: {
    title: "🏗️ أعمال الأساسات (SBC 304)",
    sbcRef: "الكود السعودي SBC 304 - الخرسانة الإنشائية",
    items: [
      { name: "خرسانة قواعد C30", unit: "م³", factor: "0.12-0.18", notes: "مقاومة 30 نيوتن/مم² - قواعد منفصلة" },
      { name: "خرسانة لبشة C30", unit: "م³", factor: "0.50-0.60", notes: "سمك 50-60 سم للبشة المسلحة" },
      { name: "خرسانة نظافة C15", unit: "م³", factor: "0.10", notes: "سمك 10 سم تحت القواعد" },
      { name: "حديد تسليح للقواعد", unit: "طن", factor: "70-90 كجم/م³", notes: "حسب SBC 304" },
      { name: "عزل مائي بيتومين", unit: "م²", factor: "1.15", notes: "طبقتين حسب SBC 202" },
    ]
  },
  structure: {
    title: "🏛️ الهيكل الإنشائي (SBC 304)",
    sbcRef: "الكود السعودي SBC 304 - الخرسانة المسلحة",
    items: [
      { name: "خرسانة أعمدة C35", unit: "م³", factor: "0.04-0.06", notes: "مقاومة 35 نيوتن/مم²" },
      { name: "خرسانة بلاطات C30", unit: "م³", factor: "0.18-0.22", notes: "بلاطات هوردي سمك 25 سم" },
      { name: "خرسانة جسور C30", unit: "م³", factor: "0.02-0.04", notes: "حسب التصميم الإنشائي" },
      { name: "حديد تسليح 10 مم", unit: "طن", factor: "15-20 كجم/م²", notes: "للكانات والتوزيع" },
      { name: "حديد تسليح 12 مم", unit: "طن", factor: "25-35 كجم/م²", notes: "للبلاطات والجسور" },
      { name: "حديد تسليح 16 مم", unit: "طن", factor: "40-55 كجم/م²", notes: "للأعمدة والجسور الرئيسية" },
      { name: "حديد تسليح 20 مم", unit: "طن", factor: "حسب التصميم", notes: "للعناصر الثقيلة" },
      { name: "سلك رباط", unit: "كجم", factor: "15 كجم/طن حديد", notes: "لربط الحديد" },
    ]
  },
  walls: {
    title: "🧱 أعمال البناء (SBC 305)",
    sbcRef: "الكود السعودي SBC 305 - أعمال البناء",
    items: [
      { name: "بلك خرساني معزول 20سم", unit: "بلكة", factor: "12.5 بلكة/م²", notes: "معزول حرارياً حسب SBC 601" },
      { name: "بلك خرساني 15سم", unit: "بلكة", factor: "12.5 بلكة/م²", notes: "للجدران الداخلية" },
      { name: "بلك خرساني 10سم", unit: "بلكة", factor: "12.5 بلكة/م²", notes: "للحواجز الخفيفة" },
      { name: "أسمنت بورتلاندي", unit: "كيس", factor: "0.6-0.8 كيس/م²", notes: "حسب مواصفات SASO" },
      { name: "رمل غسيل للبناء", unit: "م³", factor: "0.03-0.04 م³/م²", notes: "نظيف خالي من الأملاح" },
    ]
  },
  thermalInsulation: {
    title: "🛡️ العزل الحراري (SBC 601 - إلزامي)",
    sbcRef: "الكود السعودي SBC 601 - كفاءة الطاقة (إلزامي)",
    items: [
      { name: "فوم بولي يوريثان 5سم", unit: "م²", factor: "1.05-1.10", notes: "للجدران والأسقف - إلزامي" },
      { name: "فوم XPS 5سم", unit: "م²", factor: "1.05", notes: "للأسطح" },
      { name: "صوف صخري", unit: "م²", factor: "1.05", notes: "للأسقف المستعارة" },
      { name: "بلك معزول حرارياً", unit: "بلكة", factor: "12.5 بلكة/م²", notes: "بديل الفوم للجدران" },
      { name: "زجاج مزدوج للنوافذ", unit: "م²", factor: "1.0", notes: "إلزامي حسب SBC 601" },
    ]
  },
  plastering: {
    title: "🎨 أعمال التلييس",
    sbcRef: "المواصفات السعودية العامة",
    items: [
      { name: "أسمنت للتلييس", unit: "كيس", factor: "0.40-0.50 كيس/م²", notes: "طبقتين داخلي وخارجي" },
      { name: "رمل بطحاء ناعم", unit: "م³", factor: "0.025-0.03 م³/م²", notes: "مغسول ومنخول" },
      { name: "جبس بلدي", unit: "كيس", factor: "0.18-0.22 كيس/م²", notes: "للطبقة النهائية" },
      { name: "شبك تلييس مجلفن", unit: "م²", factor: "0.10-0.15", notes: "للفتحات والزوايا" },
    ]
  },
  flooring: {
    title: "🔲 بلاط الأرضيات",
    sbcRef: "مواصفات SASO للبلاط",
    items: [
      { name: "سيراميك أرضيات 60×60", unit: "م²", factor: "1.05-1.07", notes: "درجة أولى SASO", tileSize: "60×60" },
      { name: "بورسلان 60×60", unit: "م²", factor: "1.05", notes: "للصالات الرئيسية", tileSize: "60×60" },
      { name: "جرانيت أرضيات", unit: "م²", factor: "1.08-1.10", notes: "للمداخل" },
      { name: "لاصق بلاط C2", unit: "كيس", factor: "0.20-0.25 كيس/م²", notes: "حسب EN 12004" },
      { name: "روبة إيبوكسي", unit: "كجم", factor: "0.5-0.7 كجم/م²", notes: "مقاومة للرطوبة" },
    ]
  },
  wallTiles: {
    title: "🚿 بلاط الحمامات والمطابخ",
    sbcRef: "مواصفات SASO",
    items: [
      { name: "سيراميك جدران 30×60", unit: "م²", factor: "1.08-1.12", notes: "للحمامات", tileSize: "30×60" },
      { name: "سيراميك جدران 25×50", unit: "م²", factor: "1.10", notes: "للمطابخ", tileSize: "25×50" },
      { name: "لاصق جدران C2", unit: "كيس", factor: "0.25-0.30 كيس/م²", notes: "للجدران" },
      { name: "سيليكون مقاوم للفطريات", unit: "أنبوب", factor: "1 أنبوب/5م.ط", notes: "للزوايا والفواصل" },
    ]
  },
  waterproofing: {
    title: "💧 العزل المائي (SBC 202)",
    sbcRef: "الكود السعودي SBC 202 - العزل المائي",
    items: [
      { name: "عزل بيتومين للأسطح", unit: "م²", factor: "1.15-1.20", notes: "طبقتين مع التراكب" },
      { name: "عزل أكريليك للحمامات", unit: "م²", factor: "1.10", notes: "للأرضيات والجدران" },
      { name: "شريط عزل للزوايا", unit: "م.ط", factor: "محيط الحمام", notes: "لمنع التسرب" },
      { name: "برايمر بيتومين", unit: "جالون", factor: "1 جالون/10م²", notes: "طبقة تحضيرية" },
    ]
  },
  gypsum: {
    title: "⬜ أعمال الجبس بورد",
    sbcRef: "المواصفات الفنية",
    items: [
      { name: "ألواح جبس بورد 12مم", unit: "لوح", factor: "0.35-0.40 لوح/م²", notes: "للأسقف المستعارة" },
      { name: "ألواح جبس مقاوم للرطوبة", unit: "لوح", factor: "0.35 لوح/م²", notes: "للحمامات والمطابخ" },
      { name: "هيكل معدني مجلفن", unit: "م.ط", factor: "3-4 م.ط/م²", notes: "قنوات C و U" },
      { name: "معجون جبس", unit: "كيس", factor: "0.30-0.35 كيس/م²", notes: "للتشطيب" },
      { name: "شريط فواصل", unit: "م.ط", factor: "3 م.ط/م²", notes: "للوصلات" },
    ]
  },
  painting: {
    title: "🖌️ أعمال الدهانات",
    sbcRef: "مواصفات SASO للدهانات",
    items: [
      { name: "برايمر أكريليك", unit: "جالون", factor: "1 جالون/10-12م²", notes: "طبقة أساس" },
      { name: "دهان بلاستيكي داخلي", unit: "جالون", factor: "1 جالون/8-10م²", notes: "طبقتين - قابل للغسيل" },
      { name: "دهان أكريليك خارجي", unit: "جالون", factor: "1 جالون/6-8م²", notes: "مقاوم للعوامل الجوية" },
      { name: "معجون جدران", unit: "كيس", factor: "1 كيس/12-15م²", notes: "للتنعيم" },
    ]
  },
  electrical: {
    title: "⚡ الأعمال الكهربائية (SBC 401)",
    sbcRef: "الكود السعودي SBC 401 - التمديدات الكهربائية",
    items: [
      { name: "أسلاك نحاس 1.5مم²", unit: "م.ط", factor: "10-12 م.ط/م²", notes: "للإنارة" },
      { name: "أسلاك نحاس 2.5مم²", unit: "م.ط", factor: "18-22 م.ط/م²", notes: "للبرايز العادية" },
      { name: "أسلاك نحاس 4مم²", unit: "م.ط", factor: "6-10 م.ط/م²", notes: "للمكيفات" },
      { name: "أسلاك نحاس 6مم²", unit: "م.ط", factor: "2-4 م.ط/م²", notes: "للأحمال الثقيلة" },
      { name: "خراطيم PVC", unit: "م.ط", factor: "28-35 م.ط/م²", notes: "للتمديدات" },
      { name: "لوحة توزيع 24 خط", unit: "لوحة", factor: "1 لوحة/شقة", notes: "مع القواطع" },
    ]
  },
  plumbing: {
    title: "🚰 الأعمال الصحية (SBC 501)",
    sbcRef: "الكود السعودي SBC 501 - السباكة",
    items: [
      { name: "مواسير PPR PN20 - 20مم", unit: "م.ط", factor: "3.5-4.5 م.ط/م²", notes: "للمياه الباردة" },
      { name: "مواسير PPR PN20 - 25مم", unit: "م.ط", factor: "2.5-3.5 م.ط/م²", notes: "للخطوط الرئيسية" },
      { name: "مواسير PVC صرف 4\"", unit: "م.ط", factor: "0.5-0.7 م.ط/م²", notes: "للصرف الرئيسي" },
      { name: "مواسير PVC صرف 2\"", unit: "م.ط", factor: "1-1.5 م.ط/م²", notes: "لصرف الأحواض" },
      { name: "سخان مياه كهربائي", unit: "سخان", factor: "1-2 سخان/شقة", notes: "حسب عدد الحمامات" },
    ]
  },
  doors: {
    title: "🚪 الأبواب والنوافذ",
    sbcRef: "المواصفات السعودية + SBC 601",
    items: [
      { name: "باب خشب داخلي", unit: "باب", factor: "5-7 باب/شقة", notes: "للغرف" },
      { name: "باب ألمنيوم حمام", unit: "باب", factor: "2-3 باب/شقة", notes: "مقاوم للرطوبة" },
      { name: "باب رئيسي حديد", unit: "باب", factor: "1 باب/شقة", notes: "باب أمان" },
      { name: "نوافذ ألمنيوم مزدوج", unit: "م²", factor: "8-12% من مساحة الشقة", notes: "زجاج مزدوج إلزامي SBC 601" },
    ]
  },
  selko: {
    title: "🪟 السلكو والإطارات",
    sbcRef: "التشطيبات الداخلية",
    items: [
      { name: "سلكو خشب MDF", unit: "م.ط", factor: "5 م.ط/باب", notes: "إطارات الأبواب الداخلية" },
      { name: "سلكو ألمنيوم", unit: "م.ط", factor: "4.5 م.ط/باب", notes: "لأبواب الحمامات" },
      { name: "سلكو نوافذ رخام", unit: "م.ط", factor: "3 م.ط/نافذة", notes: "إطارات النوافذ" },
      { name: "عتبات نوافذ رخام", unit: "م.ط", factor: "1.2 م/نافذة", notes: "رخام محلي" },
    ]
  },
  entrances: {
    title: "🏛️ المداخل والبسطات",
    sbcRef: "التشطيبات",
    items: [
      { name: "رخام مداخل جرانيت", unit: "م²", factor: "15-25 م²/مدخل", notes: "للمدخل الرئيسي" },
      { name: "رخام بسطات أدوار", unit: "م²", factor: "6-10 م²/بسطة", notes: "أمام المصعد" },
      { name: "رخام عتبات أبواب", unit: "م²", factor: "0.5 م²/باب", notes: "عتبة باب الشقة" },
      { name: "وزرة رخام", unit: "م²", factor: "10سم ارتفاع", notes: "للمداخل والممرات" },
    ]
  },
  stairs: {
    title: "🪜 السلالم والدرج",
    sbcRef: "كود السلامة SBC",
    items: [
      { name: "رخام درج نائمة", unit: "م²", factor: "1.2م × 0.30م/درجة", notes: "30 سم عرض النائمة" },
      { name: "رخام درج قائمة", unit: "م²", factor: "1.2م × 0.17م/درجة", notes: "17 سم ارتفاع القائمة" },
      { name: "رخام بسطات درج", unit: "م²", factor: "2-3 م²/بسطة", notes: "بسطات الراحة" },
      { name: "درابزين ستانلس", unit: "م.ط", factor: "طول الدرج", notes: "إلزامي للسلامة" },
      { name: "كف درج مانع انزلاق", unit: "م.ط", factor: "1.2 م/درجة", notes: "للسلامة" },
    ]
  },
  roof: {
    title: "🏠 أعمال السطح",
    sbcRef: "SBC 202 + SBC 601",
    items: [
      { name: "خرسانة ميول", unit: "م³", factor: "0.10-0.15 م³/م²", notes: "متوسط سمك 10-15 سم" },
      { name: "عزل مائي بيتومين", unit: "م²", factor: "1.20", notes: "طبقتين مع 20% تراكب" },
      { name: "عزل حراري فوم", unit: "م²", factor: "1.05", notes: "إلزامي حسب SBC 601" },
      { name: "بلاط إنترلوك", unit: "قطعة", factor: "50-55 قطعة/م²", notes: "للحماية" },
    ]
  },
  parking: {
    title: "🚗 المواقف (الدور الأرضي)",
    sbcRef: "كود المواقف",
    items: [
      { name: "خرسانة أرضية C25", unit: "م³", factor: "0.15 م³/م²", notes: "سمك 15 سم" },
      { name: "شبك حديد سلك", unit: "م²", factor: "1.05", notes: "للتقوية" },
      { name: "دهان إيبوكسي أرضيات", unit: "م²", factor: "1.0", notes: "للحماية والنظافة" },
      { name: "خطوط تحديد المواقف", unit: "م.ط", factor: "حسب عدد المواقف", notes: "دهان عاكس" },
    ]
  },
};

// مثال المشروع حسب الكود السعودي
const BUILDING_EXAMPLE = {
  name: "عمارة سكنية حسب الكود السعودي",
  landArea: 900,
  totalArea: 5400,
  floorsCount: 6,
  floors: [
    { name: "الأرضي (مواقف)", area: 900, type: "مواقف سيارات" },
    { name: "الأول", area: 900, type: "4 شقق سكنية" },
    { name: "الثاني", area: 900, type: "4 شقق سكنية" },
    { name: "الثالث", area: 900, type: "4 شقق سكنية" },
    { name: "الرابع", area: 900, type: "4 شقق سكنية" },
    { name: "الملاحق", area: 900, type: "2 ملحق" },
  ],
  apartments: {
    regular: { count: 16, rooms: 4, bathrooms: 3, area: 225, desc: "شقة (4 غرف + 3 حمام + مطبخ)" },
    penthouse: { count: 2, rooms: 5, bathrooms: 4, area: 450, desc: "ملحق (5 غرف + 4 حمام + مطبخ)" },
  }
};

export default function UserGuide() {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({});
  const [loading, setLoading] = useState(false);

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const downloadPDF = async () => {
    try {
      setLoading(true);
      toast.info('جاري إنشاء ملف PDF...');
      
      const response = await axios.get(`${API}/guide/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'دليل_مواد_البناء_الكود_السعودي.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('تم تحميل الدليل بنجاح');
    } catch (error) {
      toast.error('خطأ في تحميل الدليل');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    try {
      setLoading(true);
      toast.info('جاري إنشاء ملف Excel...');
      
      const response = await axios.get(`${API}/guide/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'دليل_مواد_البناء_الكود_السعودي.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('تم تحميل الدليل بنجاح');
    } catch (error) {
      toast.error('خطأ في تحميل الدليل');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FileText className="w-6 h-6 text-emerald-600" />
            دليل مواد البناء - الكود السعودي SBC
          </h1>
          <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
            <Shield className="w-4 h-4 text-blue-500" />
            حسب الكود السعودي للبناء والمواصفات السعودية SASO
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={downloadPDF} disabled={loading} className="gap-2 bg-red-600 hover:bg-red-700">
            <Download className="w-4 h-4" />
            تحميل PDF
          </Button>
          <Button onClick={downloadExcel} disabled={loading} className="gap-2 bg-emerald-600 hover:bg-emerald-700">
            <Download className="w-4 h-4" />
            تحميل Excel
          </Button>
          <Button onClick={() => navigate('/buildings')} variant="outline" className="gap-2">
            <ArrowRight className="w-4 h-4" />
            العودة
          </Button>
        </div>
      </div>

      {/* Saudi Building Code Banner */}
      <Card className="mb-4 border-0 bg-gradient-to-r from-green-600 to-emerald-700 text-white">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Shield className="w-10 h-10" />
            <div>
              <h2 className="text-lg font-bold">الكود السعودي للبناء SBC</h2>
              <p className="text-sm opacity-90">هذا الدليل معد وفقاً لمتطلبات الكود السعودي للبناء ومواصفات SASO</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs">
            <div className="bg-white/20 rounded p-2 text-center">
              <div className="font-bold">SBC 304</div>
              <div>الخرسانة المسلحة</div>
            </div>
            <div className="bg-white/20 rounded p-2 text-center">
              <div className="font-bold">SBC 401</div>
              <div>الكهرباء</div>
            </div>
            <div className="bg-white/20 rounded p-2 text-center">
              <div className="font-bold">SBC 501</div>
              <div>السباكة</div>
            </div>
            <div className="bg-white/20 rounded p-2 text-center">
              <div className="font-bold">SBC 601</div>
              <div>كفاءة الطاقة (إلزامي)</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Building Example Card */}
      <Card className="mb-4 border-0 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-t-xl">
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            مثال: {BUILDING_EXAMPLE.name}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-blue-50 p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-blue-700">{BUILDING_EXAMPLE.landArea}</p>
              <p className="text-xs text-gray-600">مساحة الأرض (م²)</p>
            </div>
            <div className="bg-emerald-50 p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-emerald-700">{BUILDING_EXAMPLE.totalArea.toLocaleString()}</p>
              <p className="text-xs text-gray-600">المساحة الإجمالية (م²)</p>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-purple-700">{BUILDING_EXAMPLE.floorsCount}</p>
              <p className="text-xs text-gray-600">عدد الأدوار</p>
            </div>
            <div className="bg-amber-50 p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-amber-700">{BUILDING_EXAMPLE.apartments.regular.count + BUILDING_EXAMPLE.apartments.penthouse.count}</p>
              <p className="text-xs text-gray-600">عدد الوحدات</p>
            </div>
          </div>

          {/* Floors Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
            {BUILDING_EXAMPLE.floors.map((floor, idx) => (
              <div key={idx} className={`p-2 rounded-lg border text-center ${floor.type === 'مواقف سيارات' ? 'bg-gray-100 border-gray-300' : floor.type.includes('ملحق') ? 'bg-orange-50 border-orange-200' : 'bg-blue-50 border-blue-200'}`}>
                <p className="font-bold text-sm">{floor.name}</p>
                <p className="text-xs text-gray-600">{floor.area} م²</p>
                <p className="text-xs text-gray-500">{floor.type}</p>
              </div>
            ))}
          </div>

          {/* Apartment Details */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <h4 className="font-bold text-blue-800 mb-1">🏠 الشقق السكنية ({BUILDING_EXAMPLE.apartments.regular.count} شقة)</h4>
              <p className="text-sm">{BUILDING_EXAMPLE.apartments.regular.desc}</p>
              <p className="text-xs text-gray-600">المساحة: {BUILDING_EXAMPLE.apartments.regular.area} م²/شقة</p>
            </div>
            <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
              <h4 className="font-bold text-orange-800 mb-1">🏠 الملاحق ({BUILDING_EXAMPLE.apartments.penthouse.count} ملحق)</h4>
              <p className="text-sm">{BUILDING_EXAMPLE.apartments.penthouse.desc}</p>
              <p className="text-xs text-gray-600">المساحة: {BUILDING_EXAMPLE.apartments.penthouse.area} م²/ملحق</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Important Tips */}
      <Card className="mb-4 border-0 shadow-lg bg-amber-50">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-amber-800 text-lg">
            <Lightbulb className="w-5 h-5" />
            متطلبات إلزامية حسب الكود السعودي
          </CardTitle>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-2">
          <div className="flex items-start gap-2 bg-white p-2 rounded-lg">
            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <span className="text-sm"><strong>SBC 601:</strong> العزل الحراري إلزامي للجدران والأسطح</span>
          </div>
          <div className="flex items-start gap-2 bg-white p-2 rounded-lg">
            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <span className="text-sm"><strong>SBC 601:</strong> الزجاج المزدوج إلزامي للنوافذ</span>
          </div>
          <div className="flex items-start gap-2 bg-white p-2 rounded-lg">
            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <span className="text-sm"><strong>SBC 304:</strong> الحد الأدنى لمقاومة الخرسانة C25</span>
          </div>
          <div className="flex items-start gap-2 bg-white p-2 rounded-lg">
            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <span className="text-sm"><strong>SBC 202:</strong> العزل المائي للأساسات والأسطح والحمامات</span>
          </div>
        </CardContent>
      </Card>

      {/* Materials Sections */}
      <div className="space-y-3">
        {Object.entries(MATERIALS_DATA).map(([key, section]) => (
          <Card key={key} className="border-0 shadow-md overflow-hidden">
            <button
              onClick={() => toggleSection(key)}
              className="w-full p-3 flex justify-between items-center bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-150 transition-colors"
            >
              <div className="text-right">
                <h3 className="font-bold text-gray-800">{section.title}</h3>
                {section.sbcRef && (
                  <p className="text-xs text-blue-600">{section.sbcRef}</p>
                )}
              </div>
              {expandedSections[key] ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
            </button>
            
            {expandedSections[key] && (
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-800 text-white">
                      <tr>
                        <th className="p-2 text-right">المادة</th>
                        <th className="p-2 text-center">الوحدة</th>
                        <th className="p-2 text-center">المعامل</th>
                        <th className="p-2 text-right">ملاحظات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.items.map((item, idx) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="p-2 font-medium">{item.name}</td>
                          <td className="p-2 text-center">
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">{item.unit}</span>
                          </td>
                          <td className="p-2 text-center font-bold text-purple-600">{item.factor}</td>
                          <td className="p-2 text-xs text-gray-600">{item.notes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* Calculation Examples */}
      <Card className="mt-6 border-0 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-t-xl">
          <CardTitle className="flex items-center gap-2">
            <Calculator className="w-6 h-6" />
            أمثلة حسابية (أرض 900م² - 6 أدوار)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid md:grid-cols-2 gap-4">
            
            {/* الهيكل الإنشائي */}
            <div className="bg-red-50 p-4 rounded-lg border-r-4 border-red-500">
              <h4 className="font-bold text-red-800 mb-2">🏛️ الهيكل الإنشائي</h4>
              <p className="text-xs text-gray-600 mb-2">المساحة: 5,400 م²</p>
              <div className="bg-white p-3 rounded border text-sm space-y-2">
                <div><strong>خرسانة بلاطات C30:</strong><br/>5,400 × 0.20 = <strong className="text-red-700">1,080 م³</strong></div>
                <div><strong>خرسانة أعمدة C35:</strong><br/>5,400 × 0.05 = <strong className="text-red-700">270 م³</strong></div>
                <div><strong>حديد تسليح 12مم:</strong><br/>5,400 × 30 × 1.05 = <strong className="text-red-700">170 طن</strong></div>
                <div><strong>حديد تسليح 16مم:</strong><br/>5,400 × 45 × 1.05 = <strong className="text-red-700">255 طن</strong></div>
              </div>
            </div>

            {/* العزل الحراري */}
            <div className="bg-green-50 p-4 rounded-lg border-r-4 border-green-500">
              <h4 className="font-bold text-green-800 mb-2">🛡️ العزل الحراري (إلزامي SBC 601)</h4>
              <p className="text-xs text-gray-600 mb-2">الجدران: 3,600 م² | السطح: 900 م²</p>
              <div className="bg-white p-3 rounded border text-sm space-y-2">
                <div><strong>فوم جدران 5سم:</strong><br/>3,600 × 1.05 = <strong className="text-green-700">3,780 م²</strong></div>
                <div><strong>فوم سطح 5سم:</strong><br/>900 × 1.05 = <strong className="text-green-700">945 م²</strong></div>
                <div><strong>زجاج مزدوج نوافذ:</strong><br/>5,400 × 0.10 = <strong className="text-green-700">540 م²</strong></div>
              </div>
            </div>

            {/* البلاط */}
            <div className="bg-blue-50 p-4 rounded-lg border-r-4 border-blue-500">
              <h4 className="font-bold text-blue-800 mb-2">🔲 بلاط الأرضيات</h4>
              <p className="text-xs text-gray-600 mb-2">الأدوار السكنية + الملاحق: 4,500 م²</p>
              <div className="bg-white p-3 rounded border text-sm space-y-2">
                <div><strong>سيراميك 60×60:</strong><br/>4,500 × 1.07 (7% هالك) = <strong className="text-blue-700">4,815 م²</strong></div>
                <div><strong>لاصق بلاط C2:</strong><br/>4,500 × 0.25 = <strong className="text-blue-700">1,125 كيس</strong></div>
                <div><strong>روبة إيبوكسي:</strong><br/>4,500 × 0.6 = <strong className="text-blue-700">2,700 كجم</strong></div>
              </div>
            </div>

            {/* مواد الوحدات */}
            <div className="bg-purple-50 p-4 rounded-lg border-r-4 border-purple-500">
              <h4 className="font-bold text-purple-800 mb-2">🏠 مواد الشقق والملاحق</h4>
              <p className="text-xs text-gray-600 mb-2">16 شقة + 2 ملحق = 18 وحدة</p>
              <div className="bg-white p-3 rounded border text-sm space-y-2">
                <div><strong>كراسي حمام:</strong><br/>(16×3) + (2×4) = <strong className="text-purple-700">56 كرسي</strong></div>
                <div><strong>أبواب خشب داخلية:</strong><br/>(16×6) + (2×8) = <strong className="text-purple-700">112 باب</strong></div>
                <div><strong>سخانات مياه:</strong><br/>(16×2) + (2×3) = <strong className="text-purple-700">38 سخان</strong></div>
                <div><strong>لوحات توزيع:</strong><br/>18 × 1 = <strong className="text-purple-700">18 لوحة</strong></div>
              </div>
            </div>

          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="mt-6 p-4 bg-gray-100 rounded-xl text-center text-sm text-gray-600">
        <AlertTriangle className="w-5 h-5 mx-auto mb-2 text-amber-500" />
        <p>هذا الدليل للاسترشاد فقط. يُنصح بمراجعة المهندس المختص والتأكد من تطبيق متطلبات الكود السعودي للبناء SBC</p>
      </div>
    </div>
  );
}
